import { useEffect, useState } from 'react'
import { lastSeenText } from '../../shared/presence'
import { api, type PlayerActivity, type State, type StatePlayer } from '../api'

/*
 * Admin - Players, built from turn 8a of the design canvas.
 *
 * Every size here comes off that artboard rather than off the screenshot: the
 * canvas carries the real declarations, so a 34x20 toggle is 34x20 because the
 * design says so and not because a 2x PNG measured 68x40.
 *
 * The roster is the one screen where a wrong tap is expensive, so everything
 * destructive on it is deliberately slower than the rest of the app: the ✕
 * arms first and disarms itself, and the PIN toggle only ever travels one way.
 * Nothing here is a dialog - a dialog stops the whole screen to ask about one
 * row, and the row is where the question belongs.
 *
 * THE PIN TOGGLE IS ONE-WAY ON PURPOSE. The commissioner can turn a PIN OFF,
 * which clears a forgotten one, and can never turn a PIN ON, because a PIN he
 * chose would be a PIN he knows. The server enforces it - `admin/pin` accepts
 * a null and nothing else - and it is drawn here as a toggle that is simply
 * dead in its OFF position.
 */

/*
 * The design's actions are 32px tall and its toggle 34x20, all under the 44px
 * floor this app holds itself to. Rather than fatten the boxes and lose the
 * drawing, each box keeps the size it was drawn at and an invisible overlay
 * carries the TOUCH target out to 44 in both directions. The overlay reaches
 * into the 10px gutters either side, which are not themselves tappable, so
 * nothing steals a neighbour's taps.
 */
const TAP =
  "relative after:absolute after:top-1/2 after:left-1/2 after:h-11 after:w-full after:min-w-11 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']"

/** A bordered secondary action. Slate, because none of them is something you did. */
const ACTION = `${TAP} slate-num inline-flex min-h-[34px] shrink-0 items-center justify-center rounded-[3px] border border-hairline px-2.5 text-[10px] font-semibold tracking-[0.08em] text-slate uppercase`

const FIELD =
  'slate-num h-11 min-w-0 flex-1 rounded-[3px] border border-hairline bg-ground px-2 text-[15px] text-ink focus:border-gold-edge focus:outline-none'

/*
 * The two-tap ✕, in one place because two rows now use it: retiring a player
 * and turning down a request.
 *
 * Armed is filled INK, not red. Red in a scoring app reads as "wrong", and
 * neither retiring a cousin who moved away nor declining a name is an error -
 * it is just the loudest thing on that row for four seconds.
 */
const REMOVE = `${TAP} slate-num inline-flex min-h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[3px] border border-hairline text-[13px] font-medium text-faint`
const REMOVE_ARMED = `${TAP} slate-num inline-flex min-h-8 shrink-0 items-center justify-center rounded-[3px] bg-ink px-3 text-[10px] font-bold tracking-[0.08em] text-ground uppercase`

/** A request is a rough moment, not a timestamp anyone reads to the minute. */
function whenAsked(atSeconds: number): string {
  const mins = Math.max(0, (Date.now() / 1000 - atSeconds) / 60)
  if (mins < 2) return 'Just now'
  if (mins < 60) return `${Math.round(mins)}m ago`
  const hours = mins / 60
  return hours < 24 ? `${Math.round(hours)}h ago` : `${Math.round(hours / 24)}d ago`
}

export function AdminPlayers({
  state,
  onChanged,
  joinCode,
  commissionerId,
}: {
  state: State
  onChanged: () => void
  /*
   * The join code never reaches the client - the family gate would be
   * pointless if the page behind it printed the answer - so the link is drawn
   * with a placeholder until the code is handed in, and COPY stays dead until
   * then rather than putting a codeless link on somebody's clipboard.
   */
  joinCode?: string
  /*
   * Nothing in the schema says who the commissioner IS. The default is the
   * player this session is signed in as, which on a screen that already
   * required the admin cookie is him; passing it explicitly is the escape
   * hatch for the day that stops being true.
   */
  commissionerId?: string
}) {
  const boss = commissionerId ?? state.me.playerId
  const [confirming, setConfirming] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [adding, setAdding] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  /*
   * Requests are fetched here rather than read off `state`. They cannot come
   * from there: /api/state lists ACTIVE players only, which is the whole
   * reason a pending name is harmless while it waits.
   */
  const [waiting, setWaiting] = useState<{ id: string; name: string; created_at: number }[]>([])

  const readWaiting = () => {
    void api.admin.pending().then((r) => setWaiting(r.pending)).catch(() => {})
  }
  useEffect(readWaiting, [])

  /*
   * The armed ✕ disarms itself after four seconds. That number is the whole
   * pattern: long enough to be a second tap, short enough that a phone left
   * face-up on a table is not one thumb away from retiring a cousin.
   */
  useEffect(() => {
    if (!confirming) return
    const t = setTimeout(() => setConfirming(null), 4000)
    return () => clearTimeout(t)
  }, [confirming])

  useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(t)
  }, [copied])

  const run = async (fn: () => Promise<unknown>) => {
    setMsg(null)
    try {
      await fn()
      onChanged()
      return true
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'That did not work.')
      return false
    }
  }

  /* The roster refresh comes from `run`; the queue has to re-read itself. */
  const decide = async (playerId: string, approve: boolean) => {
    if (await run(() => api.admin.decide(playerId, approve))) readWaiting()
  }

  const origin = location.origin
  const link = `${origin}/join/${joinCode ?? ''}`
  const shown = `${origin.replace(/^https?:\/\//, '')}/join/${joinCode ?? '····'}`

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
    } catch {
      // Some phones refuse the clipboard outside a trusted gesture. Showing the
      // link is worse than copying it and far better than doing nothing.
      setMsg(link)
    }
  }

  const startRename = (p: StatePlayer) => {
    setConfirming(null)
    setEditing(p.id)
    setDraft(p.name)
  }

  const saveRename = async (id: string) => {
    const name = draft.trim()
    if (!name) return setEditing(null)
    if (await run(() => api.admin.player('rename', { playerId: id, name }))) setEditing(null)
  }

  const addPlayer = async () => {
    const name = draft.trim()
    if (!name) return setAdding(false)
    if (!(await run(() => api.admin.player('add', { name })))) return
    setDraft('')
    setAdding(false)
  }

  return (
    <div className="pb-2">
      <header className="flex items-baseline justify-between gap-3 px-[18px] pt-5 pb-3">
        {/* 1.2, not leading-none: the design's header is 68px tall, which is
            20 + a 36px line box + 12. Big Shoulders' own normal leading is
            taller than that and pushes the tabs 9px down. */}
        <span className="text-[30px] leading-[1.2] font-extrabold tracking-[0.03em] uppercase">
          Admin
        </span>
        <span className="slate-num text-[10.5px] font-medium tracking-[0.1em] text-mute uppercase">
          {state.players.length} Players
        </span>
      </header>

      <Activity />


      {/* The way in, for anybody whose phone has not been through the gate. */}
      <div className="slate-card mx-3 mb-3 flex items-center gap-2.5 px-3 py-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="slate-num truncate text-[12px] font-semibold text-ink">{shown}</span>
          <span className="slate-num text-[9px] tracking-[0.06em] text-mute uppercase">
            {joinCode ? 'Invite link — anyone with it can join' : 'Invite link — code not loaded'}
          </span>
        </div>
        <button
          type="button"
          disabled={!joinCode}
          onClick={() => void copy()}
          className={`${ACTION} min-h-[38px] px-3 disabled:opacity-40`}
        >
          {copied ? 'Done' : 'Copy'}
        </button>
      </div>

      {/*
       * Asked to join, and not yet anybody.
       *
       * Nothing renders when the queue is empty: a card saying "no requests"
       * is furniture on the one screen where the rows are the content. The
       * ✕ arms and disarms on the same timer as the roster's, so a request
       * and a player are turned down the same way.
       */}
      {waiting.length > 0 && (
        <section className="mb-3 px-3">
          <p className="slate-num pb-1.5 text-[9px] tracking-[0.1em] text-faint uppercase">
            Asked to join — nobody here can pick until you approve them
          </p>
          <ul className="flex flex-col gap-1.5">
            {waiting.map((w) => {
              const armed = confirming === w.id

              return (
                <li key={w.id} className="slate-card flex min-h-12 items-center gap-2.5 px-3">
                  <span className="flex min-w-0 flex-1 items-baseline gap-2">
                    <span className="truncate text-[18px] leading-none font-bold tracking-[0.02em] uppercase">
                      {w.name}
                    </span>
                    {/* Mint, because when they asked is the only TIME on this screen. */}
                    <span className="slate-num shrink-0 text-[8.5px] font-medium tracking-[0.08em] text-live-deep uppercase">
                      {whenAsked(w.created_at)}
                    </span>
                  </span>

                  <button type="button" onClick={() => void decide(w.id, true)} className={ACTION}>
                    Approve
                  </button>

                  <button
                    type="button"
                    onClick={() => (armed ? void decide(w.id, false) : setConfirming(w.id))}
                    aria-label={armed ? `Confirm turning down ${w.name}` : `Turn down ${w.name}`}
                    className={armed ? REMOVE_ARMED : REMOVE}
                  >
                    {armed ? '✕ Confirm' : '✕'}
                  </button>
                </li>
              )
            })}
          </ul>
        </section>
      )}

      <ul className="flex flex-col gap-1.5 px-3 pb-2.5">
        {state.players.map((p) => {
          const isBoss = p.id === boss
          const hasPin = p.has_pin === 1
          const armed = confirming === p.id
          const renaming = editing === p.id

          return (
            <li key={p.id} className="slate-card flex min-h-12 items-center gap-2.5 px-3">
              {renaming ? (
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void saveRename(p.id)}
                  aria-label={`Rename ${p.name}`}
                  autoFocus
                  className={FIELD}
                />
              ) : (
                <span className="flex min-w-0 flex-1 items-baseline gap-2">
                  <span className="truncate text-[18px] leading-none font-bold tracking-[0.02em] uppercase">
                    {p.name}
                  </span>
                  {/*
                   * Gold is something a person did, and running the pool is the
                   * largest of those. A chip rather than a colour on the name,
                   * so the roster still reads as one column of names.
                   */}
                  {isBoss && (
                    <span className="slate-num shrink-0 rounded-[2px] border border-gold/60 px-[5px] py-[2px] text-[8.5px] font-medium tracking-[0.08em] text-gold-text uppercase">
                      Commissioner
                    </span>
                  )}
                </span>
              )}

              {!renaming && (
                <span className="flex shrink-0 items-center gap-1.5">
                  <span
                    className={[
                      'slate-num text-[8.5px] font-medium tracking-[0.08em] uppercase',
                      hasPin ? 'text-slate' : 'text-faint',
                    ].join(' ')}
                  >
                    Pin
                  </span>
                  <button
                    type="button"
                    disabled={!hasPin}
                    onClick={() => void run(() => api.admin.pin(p.id, null))}
                    role="switch"
                    aria-checked={hasPin}
                    aria-label={`${p.name} has a PIN`}
                    className={[
                      TAP,
                      'block h-5 w-[34px] shrink-0 rounded-[3px]',
                      hasPin ? 'bg-ink' : 'border border-hairline bg-recessed',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'pointer-events-none absolute block h-3.5 w-3.5 rounded-[2px]',
                        hasPin
                          ? 'top-[3px] right-[3px] bg-ground'
                          : 'top-[2px] left-[2px] border border-hairline bg-card',
                      ].join(' ')}
                    />
                  </button>
                </span>
              )}

              <button
                type="button"
                onClick={() => (renaming ? void saveRename(p.id) : startRename(p))}
                className={ACTION}
              >
                {renaming ? 'Save' : 'Rename'}
              </button>

              {/*
               * No ✕ on the commissioner. Not because the server would refuse
               * it, but because the one person nobody else can put back is the
               * one holding the admin cookie.
               */}
              {!isBoss && !renaming && (
                <button
                  type="button"
                  onClick={() =>
                    armed
                      ? void run(() => api.admin.player('remove', { playerId: p.id }))
                      : setConfirming(p.id)
                  }
                  aria-label={armed ? `Confirm removing ${p.name}` : `Remove ${p.name}`}
                  className={armed ? REMOVE_ARMED : REMOVE}
                >
                  {armed ? '✕ Confirm' : '✕'}
                </button>
              )}
            </li>
          )
        })}

        {/* Dashed, because it is a slot rather than a row - there is nothing in it yet. */}
        <li>
          {adding ? (
            <div className="flex items-center gap-2.5">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void addPlayer()}
                placeholder="Name"
                aria-label="New player’s name"
                autoFocus
                className={`${FIELD} rounded-[6px]`}
              />
              <button type="button" onClick={() => void addPlayer()} className={ACTION}>
                Add
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setDraft('')
                setAdding(true)
              }}
              /* 48 rather than .slate-tap's 44: the slot is a row, and it
                 lines up with the rows above it. */
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-[6px] border-[1.5px] border-dashed border-pale"
            >
              <span className="text-[20px] leading-none font-bold text-slate">+</span>
              <span className="slate-num text-[11px] font-semibold tracking-[0.1em] text-slate uppercase">
                Add player
              </span>
            </button>
          )}
        </li>
      </ul>

      {msg && <p className="px-[18px] pb-2 text-[13px] text-slate">{msg}</p>}

      {/*
       * The two rules nobody can infer from the controls: why the toggle only
       * goes one way, and what "remove" actually costs. The second is the one
       * that stops a commissioner from never daring to use the ✕ at all.
       */}
      <p className="slate-num px-[18px] pb-[18px] text-[9px] tracking-[0.06em] text-faint uppercase">
        Players set their own PIN in the app — toggle off here to clear a forgotten one · Removing a
        player keeps their past weeks
      </p>
    </div>
  )
}

/*
 * Activity, and what each number is honestly worth.
 *
 * Nothing new is recorded about anybody to produce this. Windows come from the
 * freshness poll, which runs only while the page is visible; picks and their
 * timestamps are the rows themselves. It exists to answer two questions that
 * were previously unanswerable without asking somebody: has this person opened
 * it at all, and who needs a nudge.
 *
 * TIME IS APPROXIMATE and labelled so. There is no session here - nobody logs
 * out and a phone can sit on the board in a pocket - so it counts two-minute
 * windows the app was open in. That over-states a ten-second glance as two
 * minutes and is roughly right for anything longer.
 */
function Activity() {
  const [data, setData] = useState<{ now: number; players: PlayerActivity[] } | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open || data) return
    void api.admin.activity().then(setData).catch((e) =>
      setErr(e instanceof Error ? e.message : 'Could not load activity.'))
  }, [open, data])

  const dur = (m: number) => (m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`)

  return (
    <section className="mx-3 mb-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={`${ACTION} w-full justify-between px-3`}
      >
        <span>Activity</span>
        <span className="text-faint">{open ? '\u2212' : '+'}</span>
      </button>

      {open && (
        <div className="mt-2">
          {err && <p className="slate-num mb-2 text-[10px] text-slate">{err}</p>}
          {!data && !err && (
            <p className="slate-num text-[10px] tracking-[0.06em] text-faint uppercase">Loading&hellip;</p>
          )}

          {data && (
            <>
              <ul className="flex flex-col gap-1.5">
                {data.players.map((p) => {
                  const cold = p.lastSeenAt === null
                  return (
                    <li
                      key={p.id}
                      className={[
                        'slate-card flex items-center gap-3 px-3 py-2',
                        cold ? 'border-dashed' : '',
                      ].join(' ')}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] leading-none font-bold tracking-[0.02em] uppercase">
                          {p.name}
                        </span>
                        <span className="slate-num mt-1 block text-[8.5px] tracking-[0.06em] text-mute uppercase">
                          {lastSeenText(p.lastSeenAt, data.now)}
                          {' \u00b7 '}{p.logins} {p.logins === 1 ? 'open' : 'opens'}
                          {p.devices > 0 && ` \u00b7 ${p.devices} notified`}
                          {p.picksByOthers > 0 && ` \u00b7 ${p.picksByOthers} by others`}
                        </span>
                      </span>

                      <span className="shrink-0 text-right">
                        <span
                          className={[
                            'slate-num block text-[14px] leading-none font-bold',
                            p.minutesWeek > 0 ? 'text-ink' : 'text-faint',
                          ].join(' ')}
                        >
                          {p.minutesWeek > 0 ? dur(p.minutesWeek) : '\u2014'}
                        </span>
                        <span className="slate-num mt-0.5 block text-[8px] tracking-[0.08em] text-faint uppercase">
                          this wk
                        </span>
                      </span>

                      <span className="shrink-0 text-right">
                        <span className="slate-num block text-[14px] leading-none font-bold text-slate">
                          {p.picks}
                        </span>
                        <span className="slate-num mt-0.5 block text-[8px] tracking-[0.08em] text-faint uppercase">
                          picks
                        </span>
                      </span>
                    </li>
                  )
                })}
              </ul>

              <p className="slate-num mt-2 text-[8.5px] leading-[1.6] tracking-[0.05em] text-faint uppercase">
                Time is approximate &mdash; two-minute windows the app was open in, not
                measured sessions. A dashed row has never opened it.
              </p>
            </>
          )}
        </div>
      )}
    </section>
  )
}
